package gr.tech.meetinggenie.sections

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.icons.fa.FaArrowLeft
import com.varabyte.kobweb.silk.components.icons.fa.FaArrowRightFromBracket
import com.varabyte.kobweb.silk.components.icons.fa.FaBars
import com.varabyte.kobweb.silk.components.icons.fa.FaChevronLeft
import com.varabyte.kobweb.silk.components.icons.fa.FaClipboardList
import com.varabyte.kobweb.silk.components.icons.fa.FaPenToSquare
import com.varabyte.kobweb.silk.components.icons.fa.IconSize
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import gr.tech.meetinggenie.components.widgets.loadingSpinner
import gr.tech.meetinggenie.infrastructure.api.logoutUser
import gr.tech.meetinggenie.infrastructure.session.UserSession
import gr.tech.meetinggenie.navigation.SitePage
import gr.tech.meetinggenie.styles.ButtonStyle
import gr.tech.meetinggenie.styles.NavbarBrandTitleStyle
import gr.tech.meetinggenie.styles.NavbarGreetingStyle
import gr.tech.meetinggenie.styles.SIDEBAR_COLLAPSED_PX
import gr.tech.meetinggenie.styles.SIDEBAR_WIDTH_PX
import gr.tech.meetinggenie.styles.SidebarDividerStyle
import gr.tech.meetinggenie.styles.SidebarMobileBackdropStyle
import gr.tech.meetinggenie.styles.SidebarStyle
import gr.tech.meetinggenie.styles.SidebarTitleStyle
import gr.tech.meetinggenie.styles.SidebarToggleStyle
import gr.tech.meetinggenie.styles.Theme
import gr.tech.meetinggenie.styles.TopNavStyle
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

enum class MeetingSection { RESPONSES, FORM }

@Composable
fun MeetingsListTopbar() {
    val scope = rememberCoroutineScope()
    Row(
        modifier = TopNavStyle.toModifier().fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically
    ) {
        H1(NavbarBrandTitleStyle.toModifier().toAttrs()) { Text("Meeting Genie") }

        Spacer()

        if (UserSession.info.value != null) {
            SpanText(
                text = "Hello, ${UserSession.info.value!!.name}!",
                modifier = NavbarGreetingStyle.toModifier()
            )
            Span(
                attrs = ButtonStyle.toModifier().margin(0.px)
                    .onClick { scope.launch { logoutUser() } }
                    .toAttrs()
            ) { Text("Sign Out") }
        }
    }
}

@Composable
fun MeetingSidebar(
    activeSection: MeetingSection,
    isLoading: Boolean,
    submittedForm: Boolean,
    onSectionChange: (MeetingSection) -> Unit,
) {
    val scope= rememberCoroutineScope()
    val ctx= rememberPageContext()
    val breakpoint = rememberBreakpoint()
    val isMobile   = breakpoint < Breakpoint.MD

    // Default to collapsed on mobile; re-collapse whenever screen drops to mobile
    var isCollapsed by remember { mutableStateOf(isMobile) }
    LaunchedEffect(isMobile) { if (isMobile) isCollapsed = true }

    if (isMobile && !isCollapsed) {
        Div(
            attrs = SidebarMobileBackdropStyle.toModifier()
                .onClick { isCollapsed = true }
                .toAttrs()
        ) {}
    }

    Column(
        modifier = SidebarStyle.toModifier()
            .width(if (isCollapsed) SIDEBAR_COLLAPSED_PX.px else SIDEBAR_WIDTH_PX.px)
            .minHeight(100.vh)
            .padding(topBottom = 16.px, leftRight = if (isCollapsed) 7.px else 16.px)

            // Mobile expanded → fixed overlay; everything else → sticky
            .then( other =
                if (isMobile && !isCollapsed)
                    Modifier.position(Position.Fixed).top(0.px).left(0.px).zIndex(90)
                        .boxShadow(offsetX = 4.px, offsetY = 0.px, blurRadius = 16.px, color = rgba(0, 0, 0, 0.15))
                else
                    Modifier.position(Position.Sticky).top(0.px).height(100.vh)
                        .overflow(overflowY = Overflow.Auto, overflowX = Overflow.Hidden)
            ),
        horizontalAlignment = Alignment.Start,
    ) {

        // Toggle button — always visible regardless of collapse state
        Row(
            modifier = Modifier.fillMaxWidth().margin(bottom = 16.px),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            if (!isCollapsed) {
                H2(SidebarTitleStyle.toModifier().toAttrs()) { Text("Meeting Genie") }
                Spacer()
            }
            Span(
                attrs = SidebarToggleStyle.toModifier()
                    .onClick { isCollapsed = !isCollapsed }
                    .toAttrs()
            ) {
                if (isCollapsed) FaBars(size = IconSize.SM) else FaChevronLeft(size = IconSize.SM)
            }
        }

        if (!isCollapsed) {
            Span(
                attrs = ButtonStyle.toModifier()
                    .fillMaxWidth().margin(bottom = 8.px)
                    .onClick { ctx.router.tryRoutingTo(SitePage.Meetings.route) }
                    .toAttrs()
            ) {
                FaArrowLeft(modifier = Modifier.margin(right = 8.px))
                Text("Meetings")
            }

            Hr(SidebarDividerStyle.toModifier().toAttrs())

            SidebarNavButton(
                label  = "Responses",
                active = activeSection == MeetingSection.RESPONSES,
                icon   = { FaClipboardList(modifier = Modifier.margin(right = 8.px), size = IconSize.SM) },
                onClick = { onSectionChange(MeetingSection.RESPONSES) }
            )

            SidebarNavButton(
                label  = if (submittedForm) "Edit Response" else {
                    if (isLoading) {
                        ""
                    } else {
                        "Submit Response"
                    }
                },
                active = activeSection == MeetingSection.FORM,
                icon   = {
                    if (isLoading) {
                        loadingSpinner(modifier = Modifier.color(Colors.White), iconSize = IconSize.SM)
                    } else {
                        FaPenToSquare(modifier = Modifier.margin(right = 8.px), size = IconSize.SM)
                    }
                },
                onClick = { onSectionChange(MeetingSection.FORM) }
            )

            Spacer()

            Hr(SidebarDividerStyle.toModifier().toAttrs())
            Span(
                attrs = ButtonStyle.toModifier().fillMaxWidth()
                    .onClick { scope.launch { logoutUser() } }
                    .toAttrs()
            ) {
                FaArrowRightFromBracket(modifier = Modifier.rotate(180.deg).margin(right = 8.px))
                Text("Sign Out")
            }
        }
    }
}

@Composable
private fun SidebarNavButton(
    label: String,
    active: Boolean,
    icon: @Composable () -> Unit,
    onClick: () -> Unit,
) {
    Span(
        attrs = ButtonStyle.toModifier()
            .fillMaxWidth()
            .margin(bottom = 6.px)
            .then(if (active) Modifier.backgroundColor(Theme.DarkGreen.rgb) else Modifier)
            .onClick { onClick() }
            .toAttrs()
    ) {
        icon()
        Text(label)
    }
}
